import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import Cookies from 'js-cookie';
import routeConfiguration from '../../routeConfiguration';
import { pathByRouteName } from '../../util/routes';
import { getPostAuthRedirectRoute } from '../../util/postAuthRedirect';
import { apiBaseUrl } from '../../util/api';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import config from '../../config';
import { propTypes } from '../../util/types';
import { ensureCurrentUser, isWindowDefined } from '../../util/data';
import { isSignupemailValidError } from '../../util/errors';
import { createDefaultOrganizationProfile } from '../../util/organizations';
import { parse } from '../../util/urlHelpers';
import { isAgeConfirmed, signupProtectedData } from '../../util/signupProtectedData';
import {
  Page,
  NamedLink,
  NamedRedirect,
  SocialLoginButton,
  Modal,
  TermsOfService,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
} from '../../components';
import { TopbarContainer } from '../../containers';
import { ConfirmSignupForm } from '../../forms';
import { authenticationInProgress, signup, signupWithIdp } from '../../ducks/Auth.duck';
import { SignupWizard } from './SignupWizard';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';
import { manageDisableScrolling } from '../../ducks/UI.duck';

import EmailVerification from './EmailVerification';
import { FacebookLogo, GoogleLogo, LinkedInLogo } from '../../components/Button/socialLoginLogos';
import css from './SignupPage.module.css';

// Signup flow steps
export const SIGNUP_EMAIL_STEP = 'email'; // Post-signup email verification step

export class SignupPageComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tosModalOpen: false,
      authError: Cookies.get('st-autherror')
        ? JSON.parse(Cookies.get('st-autherror').replace('j:', ''))
        : null,
      authInfo: Cookies.get('st-authinfo')
        ? JSON.parse(Cookies.get('st-authinfo').replace('j:', ''))
        : null,
    };

    this.handleWizardSubmit = this.handleWizardSubmit.bind(this);
  }

  handleWizardSubmit(signupParams) {
    const { submitSignup } = this.props;
    // The form can't be sent without the age confirmation; never create an account without it
    if (!signupParams.ageConfirmed) {
      return;
    }

    const recentlyViewedListingIdsFromLocalStorage =
      (isWindowDefined() && JSON.parse(localStorage.getItem('recentlyViewedListingIds'))) || null;

    const isOrganizationRole = signupParams.role === config.userRoles['organization'];

    // Only create organization profile data for organization role
    const organizationData = isOrganizationRole
      ? createDefaultOrganizationProfile(signupParams.organizationName, signupParams.firstName)
      : null;

    const protectedData = signupProtectedData({
      email: signupParams.email,
      ageConfirmed: signupParams.ageConfirmed,
      recentlyViewedListingIds: recentlyViewedListingIdsFromLocalStorage,
    });

    const newOrgId = organizationData?.publicDataProfiles?.[0]?.id;
    const organizationPrivateData = organizationData
      ? {
          orgSubscriptions: {
            [newOrgId]: { tier: 'freemium', status: 'active' },
          },
        }
      : null;

    const params = {
      email: signupParams.email,
      password: signupParams.password,
      firstName: signupParams.firstName,
      lastName: signupParams.lastName,
      publicData: {
        role: signupParams.role,
        ...(organizationData && { organizationProfiles: organizationData.publicDataProfiles }),
        name: {
          firstName: signupParams.firstName,
          lastName: signupParams.lastName,
          displayName: `${signupParams.firstName} ${signupParams.lastName}`,
        },
      },
      protectedData,
      ...(organizationData && { organizationPrivateData }),
      ...(organizationData && { organizationName: signupParams.organizationName }),
      // Newsletter opt-in from the consent checkbox on ProfileStep —
      // SignupPage rebuilds the params bag, so without this forward
      // the boolean gets silently dropped before reaching Auth.duck.
      newsletterOptIn: !!signupParams.newsletterOptIn,
    };

    submitSignup(params);
  }

  componentDidMount() {
    // Remove the autherror cookie once the content is saved to state
    // because we don't want to show the error message e.g. after page refresh
    Cookies.remove('st-autherror');
  }

  render() {
    const {
      params,
      authInProgress,
      currentUser,
      intl,
      isAuthenticated,
      location,
      scrollingDisabled,
      signupError,
      submitSignup,
      confirmError,
      submitSingupWithIdp,
      tab,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      onResendVerificationEmail,
      onManageDisableScrolling,
    } = this.props;

    const isConfirm = tab === 'confirm';
    const locationFrom = location.state && location.state.from ? location.state.from : null;
    const authinfoFrom =
      this.state.authInfo && this.state.authInfo.from ? this.state.authInfo.from : null;
    const from = locationFrom ? locationFrom : authinfoFrom ? authinfoFrom : null;

    const fromPagePathWithoutSearch = from?.indexOf('?');
    const fromPagePath = from?.substring(0, fromPagePathWithoutSearch);
    const fromPageRoute = routeConfiguration().find(r => r.path === fromPagePath);

    const { recipientEmail: inviteEmail } = parse(from);
    const isVerificationSignupPage =
      fromPageRoute?.name === 'VerifyOrganizationInvitePage' ||
      fromPageRoute?.name === 'VerifyListingInvitePageWithSignup';
    const fillSignupFormEmailWithInviteEmail = isVerificationSignupPage ? inviteEmail : null;

    const user = ensureCurrentUser(currentUser);
    const profileImage = user.profileImage;
    const currentUserLoaded = !!user.id;

    // We only want to show the email verification dialog in the signup
    // tab if the user isn't being redirected somewhere else
    // (i.e. `from` is present). We must also check the `emailVerified`
    // flag only when the current user is fully loaded.
    // In community-only mode, skip email verification entirely
    const isBetaMode = process.env.REACT_APP_BETA_MODE === 'true';
    const showEmailVerification =
      !isBetaMode && currentUserLoaded && !user.attributes.emailVerified;

    // Already authenticated, redirect away from auth page
    if (isAuthenticated && from) {
      return <Redirect to={from} />;
    } else if (isAuthenticated && currentUserLoaded && !showEmailVerification) {
      // Single redirect helper shared with LoginPage. Lands the user on:
      //   - OrganizationHomePage for their first org (owned wins; falls
      //     back to invited memberOfOrganizations so newly-accepted
      //     invitees go straight to the org they just joined)
      //   - CreatorHomePage if publicData.role === 'creator'
      //   - LandingPage / CommunityPage otherwise (beta fallback below).
      const target = getPostAuthRedirectRoute(currentUser);
      if (target) {
        return <NamedRedirect name={target.name} params={target.params} />;
      }

      if (isBetaMode) {
        // Brand-new community signup → one-shot profile prompt
        // (Task 3). Once the user has saved or skipped, the flag
        // below routes them straight to the community feed on
        // every subsequent visit.
        const promptDone = !!currentUser?.attributes?.profile?.privateData
          ?.communityProfilePromptDone;
        return <NamedRedirect name={promptDone ? 'CommunityPage' : 'CommunityProfilePromptPage'} />;
      }
      return <NamedRedirect name="LandingPage" />;
    } else if (
      isAuthenticated &&
      currentUserLoaded &&
      showEmailVerification &&
      profileImage &&
      params.step !== SIGNUP_EMAIL_STEP &&
      params.role
    ) {
      return (
        <NamedRedirect name="SignupPage" params={{ role: params.role, step: SIGNUP_EMAIL_STEP }} />
      );
    } else if (!params.role) {
      // /join route or beta mode → go to audience signup
      // Otherwise show role selection
      const isCommunitySignup = location.pathname === '/join';
      if (isBetaMode || isCommunitySignup) {
        return <NamedRedirect name="AudienceSignupPage" />;
      }
      return <NamedRedirect name="RoleSelectionPage" />;
    }

    const confirmErrorMessage = confirmError ? (
      <div className={css.error}>
        {isSignupemailValidError(confirmError) ? (
          <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
        ) : (
          <FormattedMessage id="AuthenticationPage.signupFailed" />
        )}
      </div>
    ) : null;

    const handleSubmitConfirm = values => {
      const { email, firstName, lastName } = this.state.authInfo;
      const {
        email: newEmail,
        firstName: newFirstName,
        lastName: newLastName,
        phoneNumber,
        organizationName,
        role: selectedRole,
        ageConfirmed,
      } = values;
      // The form can't be sent without the age confirmation; never create an account without it
      if (!isAgeConfirmed(ageConfirmed)) {
        return;
      }

      // Use role from params if available, otherwise from form values (when coming from login page)
      const userRole = params.role || selectedRole;

      const recentlyViewedListingIdsFromLocalStorage =
        (isWindowDefined() && JSON.parse(localStorage.getItem('recentlyViewedListingIds'))) || null;
      const recentlyViewedListingIds = recentlyViewedListingIdsFromLocalStorage;

      // Pass email, firstName or lastName to Flex API only if user has edited them
      // and they can't be fetched directly from idp provider (e.g. Facebook)
      const authParams = {
        ...(newEmail !== email && { email: newEmail }),
        ...(newFirstName !== firstName && { firstName: newFirstName }),
        ...(newLastName !== lastName && { lastName: newLastName }),
      };

      const isOrganizationRole = userRole === config.userRoles['organization'];

      // Only create organization profile data for organization role
      const organizationData = isOrganizationRole
        ? createDefaultOrganizationProfile(organizationName, newFirstName)
        : null;

      const publicData = {
        role: userRole,
        ...(organizationData && { organizationProfiles: organizationData.publicDataProfiles }),
        name: {
          firstName: newFirstName,
          lastName: newLastName,
          displayName: `${newFirstName} ${newLastName}`,
        },
      };

      const protectedData = signupProtectedData({
        email: newEmail,
        ageConfirmed: isAgeConfirmed(ageConfirmed),
        phoneNumber,
        recentlyViewedListingIds,
      });

      const newOrgIdIdp = organizationData?.publicDataProfiles?.[0]?.id;
      const organizationPrivateDataIdp = organizationData
        ? {
            orgSubscriptions: {
              [newOrgIdIdp]: { tier: 'freemium', status: 'active' },
            },
          }
        : null;

      submitSingupWithIdp({
        ...authParams,
        publicData,
        protectedData,
        ...(organizationData && { organizationPrivateData: organizationPrivateDataIdp }),
        ...(organizationData && { organizationName }),
      });
    };

    const getDefaultRoutes = () => {
      const routes = routeConfiguration();
      const baseUrl = apiBaseUrl();

      // Route where the user should be returned after authentication
      // This is used e.g. with EditListingPage and ListingPage
      const fromParam = from ? `from=${from}` : '';

      // Default route where user is returned after successfull authentication
      const defaultReturn = pathByRouteName('LandingPage', routes);
      const defaultReturnParam = defaultReturn ? `&defaultReturn=${defaultReturn}` : '';

      // Route for confirming user data before creating a new user
      const defaultConfirm = pathByRouteName('ConfirmSignupPage', routes, { role: params.role });
      const defaultConfirmParam = defaultConfirm ? `&defaultConfirm=${defaultConfirm}` : '';

      return { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam };
    };
    const authWithFacebook = () => {
      const defaultRoutes = getDefaultRoutes();
      const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
      window.location.href = `${baseUrl}/api/auth/facebook?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
    };

    const authWithGoogle = () => {
      const defaultRoutes = getDefaultRoutes();
      const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
      window.location.href = `${baseUrl}/api/auth/google?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
    };

    const authWithLinkedIn = () => {
      const defaultRoutes = getDefaultRoutes();
      const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
      window.location.href = `${baseUrl}/api/auth/linkedin?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
    };

    const idp = this.state.authInfo
      ? this.state.authInfo.idpId.replace(/^./, str => str.toUpperCase())
      : null;

    // Form for confirming information frm IdP (e.g. Facebook)
    // before new user is created to Flex
    const confirmForm = (
      <div className={css.content}>
        <h1 className={css.signupWithIdpTitle}>
          <FormattedMessage id="AuthenticationPage.confirmSignupWithIdpTitle" values={{ idp }} />
        </h1>
        <p className={css.confirmInfoText}>
          <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
        </p>
        {confirmErrorMessage}
        <ConfirmSignupForm
          className={css.form}
          onSubmit={handleSubmitConfirm}
          inProgress={authInProgress}
          onOpenTermsOfService={() => this.setState({ tosModalOpen: true })}
          authInfo={this.state.authInfo}
          idp={idp}
          role={params.role}
        />
      </div>
    );

    // Social login buttons — surfaced on community mode too (the
    // forum/community signup needs OAuth per client spec, Task 2).
    // Per-button visibility still falls out of the env-var presence
    // checks, so any provider whose credentials aren't configured
    // stays hidden. Order is LinkedIn → Google → Facebook by spec.
    // Still suppressed on the post-OAuth verification step, where the
    // user is already inside a provider flow.
    const showFacebookLogin = !!process.env.REACT_APP_FACEBOOK_APP_ID;
    const showGoogleLogin = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const showLinkedInLogin = !!process.env.REACT_APP_LINKEDIN_CLIENT_ID;
    const showSocialLogins =
      !isVerificationSignupPage && (showFacebookLogin || showGoogleLogin || showLinkedInLogin);

    const googleButtonText = <FormattedMessage id="AuthenticationPage.signupWithGoogle" />;
    const facebookButtonText = <FormattedMessage id="AuthenticationPage.signupWithFacebook" />;
    const linkedInButtonText = <FormattedMessage id="AuthenticationPage.signupWithLinkedIn" />;

    const socialLoginButtonsMaybe = showSocialLogins ? (
      <div className={css.idpButtons}>
        <div className={css.socialButtonsOr}>
          <span className={css.socialButtonsOrText}>
            <FormattedMessage id="AuthenticationPage.or" />
          </span>
        </div>

        {showLinkedInLogin ? (
          <div className={css.socialButtonWrapper}>
            <SocialLoginButton onClick={() => authWithLinkedIn()}>
              <span className={css.buttonIcon}>{LinkedInLogo}</span>
              {linkedInButtonText}
            </SocialLoginButton>
          </div>
        ) : null}

        {showGoogleLogin ? (
          <div className={css.socialButtonWrapper}>
            <SocialLoginButton onClick={() => authWithGoogle()}>
              <span className={css.buttonIcon}>{GoogleLogo}</span>
              {googleButtonText}
            </SocialLoginButton>
          </div>
        ) : null}

        {showFacebookLogin ? (
          <div className={css.socialButtonWrapper}>
            <SocialLoginButton onClick={() => authWithFacebook()}>
              <span className={css.buttonIcon}>{FacebookLogo}</span>
              {facebookButtonText}
            </SocialLoginButton>
          </div>
        ) : null}
      </div>
    ) : null;

    const loginSeparator = (
      <div className={css.loginSection}>
        <p className={css.loginQuestion}>
          <FormattedMessage id="AuthenticationPage.alreadyHaveAccount" />
        </p>
        <NamedLink className={css.loginLink} name="LoginPage">
          <FormattedMessage id="AuthenticationPage.loginLink" />
        </NamedLink>
      </div>
    );

    // Get signup title based on user role
    const getSignupTitle = () => {
      // In community-only mode, show simplified title
      if (process.env.REACT_APP_BETA_MODE === 'true') {
        return (
          <FormattedMessage
            id="AuthenticationPage.signupCommunityTitle"
            values={{ siteTitle: config.siteTitle }}
          />
        );
      }
      if (params.role === config.userRoles['audience']) {
        return <FormattedMessage id="AuthenticationPage.signupAsAudienceTitle" />;
      } else if (params.role === config.userRoles['organization']) {
        return <FormattedMessage id="AuthenticationPage.signupAsOrganizationTitle" />;
      } else {
        return <FormattedMessage id="AuthenticationPage.signupAsCreatorTitle" />;
      }
    };

    // Invite email content
    const inviteContentMaybe = fillSignupFormEmailWithInviteEmail ? (
      <div className={css.attentionBox}>
        <p className={css.attentionText}>
          <FormattedMessage
            id="AuthenticationPage.inviteSignup"
            values={{ email: <strong>{inviteEmail}</strong> }}
          />
        </p>
      </div>
    ) : null;

    // Signup wizard with multi-step flow
    const signupWizardContent = (
      <div className={css.content}>
        <SignupWizard
          className={css.signupForm}
          role={params.role}
          initialEmail={fillSignupFormEmailWithInviteEmail || ''}
          emailLocked={!!fillSignupFormEmailWithInviteEmail}
          authInProgress={authInProgress}
          signupError={signupError}
          onSubmit={this.handleWizardSubmit}
          onOpenTermsOfService={() => this.setState({ tosModalOpen: true })}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          currentUser={currentUser}
          socialLoginButtons={socialLoginButtonsMaybe}
          loginLink={loginSeparator}
          title={getSignupTitle()}
          inviteContent={inviteContentMaybe}
        />
      </div>
    );

    const formContent = isConfirm ? confirmForm : signupWizardContent;

    const emailVerificationContent = (
      <EmailVerification
        user={user}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
        onResendVerificationEmail={onResendVerificationEmail}
      />
    );

    // Only show email verification after signup (profile image step removed)
    const signupFlowContent = showEmailVerification ? emailVerificationContent : formContent;

    const siteTitle = config.siteTitle;
    const schemaTitle = intl.formatMessage(
      { id: 'AuthenticationPage.schemaTitleSignup' },
      { siteTitle }
    );

    return (
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'WebPage',
          name: schemaTitle,
        }}
      >
        <LayoutSingleColumn>
          <LayoutWrapperTopbar>
            <TopbarContainer />
          </LayoutWrapperTopbar>
          <LayoutWrapperMain className={css.layoutWrapperMain}>
            <div className={css.root}>{signupFlowContent}</div>
          </LayoutWrapperMain>
          <LayoutWrapperFooter>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
        <Modal
          id="AuthenticationPage.tos"
          isOpen={this.state.tosModalOpen}
          onClose={() => this.setState({ tosModalOpen: false })}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.termsWrapper}>
            <h2 className={css.termsHeading}>
              <FormattedMessage id="AuthenticationPage.termsHeading" />
            </h2>
            <TermsOfService />
          </div>
        </Modal>
      </Page>
    );
  }
}

SignupPageComponent.defaultProps = {
  currentUser: null,
  signupError: null,
  confirmError: null,
  tab: 'signup',
  sendVerificationEmailError: null,
  showSocialLoginsForTests: false,
};

const { bool, func, object, oneOf, shape } = PropTypes;

SignupPageComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  isAuthenticated: bool.isRequired,
  scrollingDisabled: bool.isRequired,
  signupError: propTypes.error,
  confirmError: propTypes.error,

  submitSignup: func.isRequired,
  tab: oneOf(['signup', 'confirm']),

  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  onManageDisableScrolling: func.isRequired,

  // from withRouter
  location: shape({ state: object }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, signupError, confirmError } = state.Auth;
  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
    confirmError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  };
};

const mapDispatchToProps = dispatch => ({
  submitSignup: params => dispatch(signup(params)),
  submitSingupWithIdp: params => dispatch(signupWithIdp(params)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SignupPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(SignupPageComponent);

export default SignupPage;
